import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasMapsKey, reverseGeocode } from '../lib/maps';
import { formatCityState } from '../lib/geo';
import { DEFAULT_RADIUS, LOCATION_STORAGE_KEY, MAX_RADIUS } from '../lib/constants';

function placeLabel(loc) {
  if (!loc) return '';
  const formatted = formatCityState(loc.city, loc.state);
  if (formatted) return formatted;
  if (loc.label && loc.label !== 'Near you' && loc.label !== 'Nearby') return loc.label;
  return '';
}

function readStored() {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.radiusMiles != null) {
      parsed.radiusMiles = Math.min(Math.max(Number(parsed.radiusMiles) || DEFAULT_RADIUS, 1), MAX_RADIUS);
    }
    return parsed;
  } catch {
    return null;
  }
}

export function useBrowseLocation() {
  const [location, setLocationState] = useState(() => readStored());
  const [radiusMiles, setRadiusState] = useState(() => {
    const stored = readStored()?.radiusMiles;
    return stored != null ? Math.min(stored, MAX_RADIUS) : DEFAULT_RADIUS;
  });
  const resolving = useRef(false);

  const setLocation = useCallback((next) => {
    setLocationState((prev) => {
      const radius = Math.min(
        Math.max(next.radiusMiles ?? prev?.radiusMiles ?? DEFAULT_RADIUS, 1),
        MAX_RADIUS,
      );
      const city = next.city || '';
      const state = next.state || '';
      const label = placeLabel({ ...next, city, state }) || next.label || '';
      const merged = {
        lat: next.lat,
        lng: next.lng,
        label,
        city,
        state,
        radiusMiles: radius,
      };
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(merged));
      if (next.radiusMiles != null) setRadiusState(radius);
      return merged;
    });
  }, []);

  const setRadiusMiles = useCallback((miles) => {
    const clamped = Math.min(Math.max(miles, 1), MAX_RADIUS);
    setRadiusState(clamped);
    setLocationState((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, radiusMiles: clamped };
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  }, []);

  useEffect(() => {
    if (!location) {
      if (!navigator.geolocation) return undefined;
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          try {
            if (hasMapsKey()) {
              const place = await reverseGeocode(lat, lng);
              setLocation({ ...place, radiusMiles: DEFAULT_RADIUS });
              return;
            }
          } catch {
            // fall through
          }
          setLocation({
            lat,
            lng,
            label: '',
            city: '',
            state: '',
            radiusMiles: DEFAULT_RADIUS,
          });
        },
        () => {},
        { enableHighAccuracy: false, timeout: 8000 },
      );
      return undefined;
    }

    const needsName =
      hasMapsKey() &&
      location.lat &&
      location.lng &&
      !placeLabel(location);
    if (!needsName || resolving.current) return undefined;
    resolving.current = true;
    reverseGeocode(location.lat, location.lng)
      .then((place) => {
        setLocation({
          ...location,
          ...place,
          radiusMiles: location.radiusMiles ?? DEFAULT_RADIUS,
        });
      })
      .catch(() => {})
      .finally(() => {
        resolving.current = false;
      });
    return undefined;
  }, [location, setLocation]);

  const label = useMemo(() => {
    if (!location) return 'Set location';
    return placeLabel(location) || 'Set location';
  }, [location]);

  return {
    location,
    radiusMiles,
    label,
    setLocation,
    setRadiusMiles,
  };
}
